/**
 * The date the page opens on (Barna, 10 October 2026): never one the slot
 * rules refuse.
 *
 * The server suggests ten business days ahead and the page puts 12:00 beside
 * it. Run 7's slot rules give a Monday 12:00 start no delivery slot — the
 * morning slot is `monday_before_12`, the afternoon one `ends_after_start` —
 * so a suggestion that fell on a Monday opened the page on *Erre a kezdési
 * időpontra nincs kiszállítási sávunk.* The rule now: keep 12:00, and take the
 * first weekday on or after the suggestion where 12:00 has an offered slot.
 *
 * The page asks `menu_context` for each date it tries, which it does anyway
 * for the date on screen, so the answer is the server's own `day_slots` and
 * not a second copy of the rules. The search belongs to the opening only:
 * it stops the moment the customer touches the date, the start or the
 * fulfilment, and it never runs again.
 */

import type { MenuContext } from '../lib/menuContext.ts'

export const OPENING_START = '12:00'
/** Weekdays tried before giving up and leaving the page where it is. */
export const OPENING_MAX_STEPS = 60

export type OpeningStep = { kind: 'wait' } | { kind: 'done' } | { kind: 'next'; date: string }

export interface OpeningInput {
  date: string
  start: string
  fulfilment: string
  context: Pick<MenuContext, 'event_date' | 'slots'> & { ok?: boolean } | null
  /** True when `context` is the answer to the page's current question. */
  fresh: boolean
  steps: number
}

/** The next Monday-to-Friday after an ISO date. A weekend is pickup only
 *  (§8.1), and stepping onto one would switch the page to pickup. */
export function nextWeekday(iso: string): string {
  const day = new Date(`${iso}T12:00:00Z`)
  do {
    day.setUTCDate(day.getUTCDate() + 1)
  } while (day.getUTCDay() === 0 || day.getUTCDay() === 6)
  return day.toISOString().slice(0, 10)
}

export function openingStep(input: OpeningInput): OpeningStep {
  // The customer has made a choice of their own: the opening is over.
  if (input.start !== OPENING_START || input.fulfilment !== 'delivery') return { kind: 'done' }
  if (!input.fresh || input.context === null || input.context.event_date !== input.date) {
    return { kind: 'wait' }
  }
  const offered = input.context.ok !== false && (input.context.slots ?? []).some((slot) => slot.offered)
  if (offered) return { kind: 'done' }
  if (input.steps >= OPENING_MAX_STEPS) return { kind: 'done' }
  return { kind: 'next', date: nextWeekday(input.date) }
}

/** What the page remembers about its own opening. `date` is the date the page
 *  itself last put in the field. */
export interface OpeningState {
  active: boolean
  date: string | null
  steps: number
}

/**
 * One pass of the page's effect, as a pure function. The case it exists for:
 * the effect runs in the same commit that asked for the suggested date, while
 * the field is still empty. That is not the customer choosing a different
 * date and must not end the search; the first deployed version read it as
 * exactly that and opened on Monday 26 October all the same.
 */
export function advanceOpening(
  opening: OpeningState,
  input: Omit<OpeningInput, 'steps'>,
): { next: OpeningState; setDate: string | null } {
  if (!opening.active || input.date === '') return { next: opening, setDate: null }
  if (input.date !== opening.date) return { next: { ...opening, active: false }, setDate: null }
  const step = openingStep({ ...input, steps: opening.steps })
  if (step.kind === 'done') return { next: { ...opening, active: false }, setDate: null }
  if (step.kind === 'next') {
    return { next: { active: true, date: step.date, steps: opening.steps + 1 }, setDate: step.date }
  }
  return { next: opening, setDate: null }
}
